import React, { useEffect, useRef } from 'react';

const SurveyPreviewModal = ({ requestData, onClose }) => {
    const dialogRef = useRef(null);
    const closeButtonRef = useRef(null);

    useEffect(() => {
        if (!requestData) return undefined;

        const previousActive = document.activeElement;
        if (closeButtonRef.current) {
            closeButtonRef.current.focus();
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
                return;
            }

            if (event.key !== 'Tab' || !dialogRef.current) return;

            const focusableElements = dialogRef.current.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (focusableElements.length === 0) return;

            const first = focusableElements[0];
            const last = focusableElements[focusableElements.length - 1];
            const active = document.activeElement;

            if (event.shiftKey && active === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (previousActive && typeof previousActive.focus === 'function') {
                previousActive.focus();
            }
        };
    }, [requestData, onClose]);

    if (!requestData) return null;

    const { metadata, questions } = requestData;
    const nonHeadingQuestions = questions.filter(q => q.question_type !== 'Heading');
    const totalNonHeadings = nonHeadingQuestions.length;
    const numbering = {};
    nonHeadingQuestions.forEach((q, i) => { numbering[q.question_id] = i + 1; });

    return (
        <div
            className="fixed inset-0 bg-gray-900 bg-opacity-75 z-50 flex items-center justify-center m-0"
            style={{ marginTop: 0 }} // fallback
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="survey-preview-title"
                className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
                <div className="p-6 border-b sticky top-0 bg-white">
                    <h2 id="survey-preview-title" className="text-2xl font-bold text-indigo-700">Preview: {metadata.module_name}</h2>
                    <p className="text-sm text-gray-500">
                        Total Questions: {totalNonHeadings} | Leader: {metadata.leader_name} | Status: {metadata.status}
                    </p>
                </div>

                <div className="p-6 space-y-4">
                    {questions.map((q) => (
                        <div key={q.question_id} className="p-3 border rounded-md bg-gray-50">
                            {q.question_type === 'Heading' ? (
                                <span className="font-mono text-xs text-gray-400 mr-2"> </span>
                            ) : (
                                <span className="font-mono text-xs text-gray-400 mr-2">[{numbering[q.question_id]}]</span>
                            )}
                            <span className={`font-semibold ${q.question_type === 'Heading' ? 'text-lg text-indigo-600' : 'text-gray-800'}`}>
                                {q.question_text}
                            </span>
                            <span className="ml-3 text-xs font-medium px-2 py-0.5 rounded text-indigo-700 bg-indigo-100">
                                {q.question_type}
                            </span>

                            {q.question_type !== 'Heading' && (
                                q.is_mandatory ? (
                                    <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded text-red-700 bg-red-100">Mandatory</span>
                                ) : (
                                    <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded text-green-700 bg-green-100">Optional</span>
                                )
                            )}

                        </div>
                    ))}
                </div>

                <div className="p-4 border-t sticky bottom-0 bg-white">
                    <button
                        ref={closeButtonRef}
                        type="button"
                        onClick={onClose}
                        className="w-full bg-indigo-600 text-white font-semibold py-2 rounded-lg hover:bg-indigo-700 transition"
                    >
                        Close Preview
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SurveyPreviewModal;